import { Dialog, Transition } from "@headlessui/react"
import cn from "classnames"
import React, { type MutableRefObject } from "react"
import { CloseLarge } from "@/skylticons"

interface ModalContextProps {
  padding?: "default" | "small"
  open?: boolean
  disableClose?: boolean
  setOpen: (open: boolean) => void
}

const ModalContext = React.createContext<ModalContextProps>({ setOpen: () => null })
const useModalContext = () => React.useContext(ModalContext)

interface ModalTitleProps {
  children?: React.ReactNode
  center?: boolean
}

const ModalTitle: React.FC<ModalTitleProps> = ({ children, center }) => {
  return <h3 className={cn("mb-6 pr-12 text-2xl font-semibold leading-6", { "text-center": center })}>{children}</h3>
}

interface ModalBodyProps {
  children?: React.ReactNode
  className?: string
}

const ModalBody: React.FC<ModalBodyProps> = ({ children, className }) => {
  const { padding } = useModalContext()

  return (
    <div
      className={cn(
        "overflow-y-auto bg-white py-6 text-sm first-of-type:rounded-t-2xl sm:py-8",
        {
          "px-4 sm:px-8": padding === "default" || !padding,
          "px-4 sm:px-6": padding === "small",
        },
        className
      )}
    >
      {children}
    </div>
  )
}

interface ModalFooterProps {
  children?: React.ReactNode
}

const ModalFooter: React.FC<ModalFooterProps> = ({ children }) => {
  return (
    <footer className="sticky bottom-0 flex justify-between rounded-b-lg bg-ds9-50 p-4 after:absolute after:inset-0 after:-z-10 after:shadow-[0_0_0_1px_rgba(0,0,0,0.07)] dark:bg-ds9-900 dark:after:shadow-[0_0_0_1px_hsla(0,0%,100%,0.2)]">
      {children}
    </footer>
  )
}

interface ModalCloseProps {
  children?: React.ReactNode
  className?: string
}

const ModalClose: React.FC<ModalCloseProps> = ({ children, className }) => {
  const { padding, setOpen } = useModalContext()

  return (
    <button
      className={cn(
        "button button-large button-subtle icon-only absolute mb-0",
        {
          "right-4 top-4 sm:right-6 sm:top-6": padding === "default" || !padding,
          "right-3 top-3 sm:right-4 sm:top-4": padding === "small",
        },
        className
      )}
      type="button"
      onClick={() => setOpen(false)}
    >
      {children || <CloseLarge />}
    </button>
  )
}

interface ModalProps {
  padding?: "default" | "small"
  children?: React.ReactNode
  width?: string
  static?: boolean
  open?: boolean
  disableClose?: boolean
  setOpen: (open: boolean) => void
  initialFocus?: MutableRefObject<HTMLElement | null>
  className?: string
}

const Modal: React.FC<ModalProps> & {
  Body: typeof ModalBody
  Footer: typeof ModalFooter
  Title: typeof ModalTitle
  CloseButton: typeof ModalClose
} = ({ children, width, static: staticDialog, open, setOpen, padding, disableClose, initialFocus, className }) => {
  return (
    <Transition appear show={open} as={React.Fragment}>
      <Dialog
        static={staticDialog}
        as="div"
        className={cn("relative z-40", className)}
        open={open || staticDialog}
        onClose={disableClose ? () => null : setOpen}
        initialFocus={initialFocus}
      >
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-start justify-center px-3 py-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-10"
              enterTo="opacity-100 translate-y-0"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-10"
            >
              <Dialog.Panel
                className="relative flex max-h-[95vh] transform flex-col overflow-hidden rounded-2xl text-left shadow-[0px_2px_4px_rgba(20,33,61,0.05),0px_8px_16px_rgba(20,33,61,0.15)] transition-all"
                style={{ width }}
              >
                <ModalContext.Provider value={{ padding, open, disableClose, setOpen }}>
                  {children}
                </ModalContext.Provider>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

Modal.Title = ModalTitle
Modal.Body = ModalBody
Modal.Footer = ModalFooter
Modal.CloseButton = ModalClose

export default Modal
